import { useEffect, useRef, useState } from "react";
import { Building2, ChevronDown, Layers, Columns3, MousePointerClick } from "lucide-react";
import { useTranslation } from "react-i18next";

const PULSE_KEYFRAMES = `
@keyframes sida-toggle-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.55; }
}
@keyframes sida-hint-float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-3px); }
}
`;

const pick = (lang: string, ar: string, en: string, fr: string) =>
  lang.startsWith("ar") ? ar : lang.startsWith("fr") ? fr : en;

// Illustrative demo data — used only for the marketing showcase.
const companies = [
  { id: "trade", nameAr: "شركة التجارة", nameEn: "Trading Co.", nameFr: "Sté Commerce" }, // i18n-allow: tri-locale data / Urdu-only demo
  { id: "resto", nameAr: "شركة المطاعم", nameEn: "Restaurants Co.", nameFr: "Sté Restaurants" }, // i18n-allow: tri-locale data / Urdu-only demo
] as const;

type LineKind = "normal" | "gross" | "net";
interface Line {
  key: string;
  labelAr: string;
  labelEn: string;
  labelFr: string;
  values: [number, number]; // [trade, resto]
  kind: LineKind;
}

const lines: Line[] = [
  { key: "revenue", labelAr: "إجمالي الإيرادات", labelEn: "Total Revenue", labelFr: "Chiffre d'affaires", values: [480000, 320000], kind: "normal" }, // i18n-allow: tri-locale data / Urdu-only demo
  { key: "cogs", labelAr: "تكلفة المبيعات", labelEn: "Cost of Sales", labelFr: "Coût des ventes", values: [300000, 180000], kind: "normal" }, // i18n-allow: tri-locale data / Urdu-only demo
  { key: "gross_profit", labelAr: "مجمل الربح", labelEn: "Gross Profit", labelFr: "Marge brute", values: [180000, 140000], kind: "gross" }, // i18n-allow: tri-locale data / Urdu-only demo
  { key: "opex", labelAr: "مصروفات التشغيل", labelEn: "Operating Expenses", labelFr: "Charges d'exploitation", values: [120000, 95000], kind: "normal" }, // i18n-allow: tri-locale data / Urdu-only demo
  { key: "net_income", labelAr: "صافي الدخل", labelEn: "Net Income", labelFr: "Résultat net", values: [60000, 45000], kind: "net" }, // i18n-allow: tri-locale data / Urdu-only demo
];

const fmt = (n: number) => n.toLocaleString("en-US");

type Mode = "separate" | "consolidated";
type Filter = "all" | (typeof companies)[number]["id"];

export function MultiCompanyIncomeStatement() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "ar").split("-")[0];
  const isRtl = lang.startsWith("ar");

  const [mode, setMode] = useState<Mode>("separate");
  const [filter, setFilter] = useState<Filter>("all");
  const [visibleMode, setVisibleMode] = useState<Mode>("separate");
  const [visibleFilter, setVisibleFilter] = useState<Filter>("all");
  const [opacity, setOpacity] = useState(1);
  const [isInView, setIsInView] = useState(false);
  const [userInteracted, setUserInteracted] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = componentRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsInView(true);
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (mode === visibleMode && filter === visibleFilter) return;
    setOpacity(0);
    const t = setTimeout(() => {
      setVisibleMode(mode);
      setVisibleFilter(filter);
      setOpacity(1);
    }, 180);
    return () => clearTimeout(t);
  }, [mode, filter, visibleMode, visibleFilter]);

  // Auto-demo: on first view, briefly toggle to consolidated then back, to prove interactivity.
  useEffect(() => {
    if (!isInView || userInteracted) return;
    const t1 = setTimeout(() => { if (!userInteracted) setMode("consolidated"); }, 1200);
    const t2 = setTimeout(() => { if (!userInteracted) setMode("separate"); }, 2600);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [isInView, userInteracted]);

  const companyName = (c: (typeof companies)[number]) => pick(lang, c.nameAr, c.nameEn, c.nameFr);

  // Column definitions based on the (lagged) visible state
  const cols: { key: string; label: string; getValue: (l: Line) => number }[] =
    visibleMode === "consolidated"
      ? [
          {
            key: "consolidated",
            label: pick(lang, "الإجمالي المجمّع", "Consolidated Total", "Total consolidé"),
            getValue: (l) => l.values.reduce((a, b) => a + b, 0),
          },
        ]
      : visibleFilter === "all"
        ? companies.map((c, idx) => ({
            key: c.id,
            label: companyName(c),
            getValue: (l) => l.values[idx],
          }))
        : (() => {
            const idx = companies.findIndex((c) => c.id === visibleFilter);
            return [
              {
                key: companies[idx].id,
                label: companyName(companies[idx]),
                getValue: (l) => l.values[idx],
              },
            ];
          })();

  const badge =
    visibleMode === "consolidated"
      ? pick(lang, "مجمّع", "Consolidated", "Consolidé")
      : visibleFilter === "all"
        ? pick(lang, "شركتان", "Two companies", "Deux sociétés")
        : (() => {
            const c = companies.find((c) => c.id === visibleFilter);
            return c ? companyName(c) : "";
          })();

  const currentCompanyLabel =
    filter === "all"
      ? pick(lang, "كل الشركات", "All companies", "Toutes les sociétés")
      : (() => {
          const c = companies.find((c) => c.id === filter);
          return c ? companyName(c) : "";
        })();

  const textAlignStart: React.CSSProperties = { textAlign: isRtl ? "right" : "left" };

  return (
    <div
      ref={componentRef}
      dir={isRtl ? "rtl" : "ltr"}
      className="mx-auto w-full max-w-5xl"
      style={{ fontFamily: "'IBM Plex Sans Arabic', 'IBM Plex Fallback', system-ui, sans-serif" }}
    >
      
      <style dangerouslySetInnerHTML={{ __html: PULSE_KEYFRAMES }} />
      <div
        className="rounded-2xl border overflow-hidden bg-white"
        style={{
          borderColor: "#E6ECF3",
          boxShadow: "0 30px 70px -30px rgba(16,52,91,0.35)",
        }}
      >
        {/* Browser chrome */}
        <div
          className="flex items-center gap-2 px-4 py-2.5 border-b"
          style={{ borderColor: "#EEF2F6", background: "#FAFCFE" }}
        >
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#4CD964" }} />
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#FFCC00" }} />
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: "#FF3B30" }} />
          <div
            className="mx-auto text-xs tracking-wide"
            dir="ltr"
            style={{ color: "#8698AC" }}
          >
            sida.one / reports / income-statement
          </div>
        </div>

        {/* Toolbar */}
        <div
          className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 px-5 py-4 border-b"
          style={{ borderColor: "#F0F4F8" }}
        >
          {/* Segmented toggle */}
          <div className="relative self-start">
            {/* Hint bubble — disappears after user interacts */}
            {!userInteracted && isInView && (
              <div
                className="absolute -top-7 start-0 flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full pointer-events-none whitespace-nowrap"
                style={{
                  background: "#1C75BC",
                  color: "#FFFFFF",
                  boxShadow: "0 6px 14px -6px rgba(28,117,188,0.55)",
                  animation: "sida-hint-float 1.6s ease-in-out infinite",
                }}
              >
                <MousePointerClick className="h-3 w-3" />
                {pick(lang, "بدّل بضغطة", "Toggle in one click", "Bascule en un clic")}
              </div>
            )}
            <div
              role="group"
              aria-label={pick(lang, "طريقة عرض القوائم", "Statement view mode", "Mode d'affichage des états")}
              className="inline-flex rounded-lg p-1"
              style={{ background: "#F1F5F9" }}
            >
              {(
                [
                  { id: "consolidated", label: pick(lang, "مجمّع", "Consolidated", "Consolidé"), Icon: Layers },
                  { id: "separate", label: pick(lang, "منفصل", "Separate", "Séparé"), Icon: Columns3 },
                ] as { id: Mode; label: string; Icon: typeof Layers }[]
              ).map((b) => {
                const active = mode === b.id;
                const Icon = b.Icon;
                return (
                  <button
                    key={b.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => { setUserInteracted(true); setMode(b.id); }}
                    onMouseEnter={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = "#E2ECF6";
                        e.currentTarget.style.color = "#1C75BC";
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!active) {
                        e.currentTarget.style.background = "transparent";
                        e.currentTarget.style.color = "#5A7594";
                        e.currentTarget.style.transform = "translateY(0)";
                      }
                    }}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm rounded-md cursor-pointer"
                    style={{
                      background: active ? "#1C75BC" : "transparent",
                      color: active ? "#FFFFFF" : "#5A7594",
                      fontWeight: active ? 700 : 500,
                      boxShadow: active ? "0 6px 16px -6px rgba(28,117,188,0.65)" : undefined,
                      transition: "background 180ms ease, color 180ms ease, transform 180ms ease, box-shadow 180ms ease",
                      animation: (!active && isInView && !userInteracted) ? "sida-toggle-pulse 1.6s ease-in-out infinite" : undefined,
                    }}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {b.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Company picker */}
          <label
            className="group relative flex items-center gap-3 rounded-xl border px-3 py-2 sm:min-w-[220px] cursor-pointer transition-all"
            title={pick(lang, "اضغط لاختيار شركة أخرى", "Click to pick another company", "Cliquez pour choisir une autre société")}
            style={{ borderColor: "#E6ECF3", background: "#FFFFFF" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#1C75BC";
              e.currentTarget.style.background = "#F6FAFD";
              e.currentTarget.style.boxShadow = "0 0 0 3px rgba(28,117,188,0.12)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#E6ECF3";
              e.currentTarget.style.background = "#FFFFFF";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            <span
              className="flex h-9 w-9 items-center justify-center rounded-lg shrink-0"
              style={{ background: "#EAF2FB", color: "#1C75BC" }}
            >
              <Building2 className="h-4 w-4" />
            </span>
            <span className="flex flex-col leading-tight flex-1 min-w-0" style={textAlignStart}>
              <span className="text-[11px]" style={{ color: "#8698AC" }}>
                {pick(lang, "الشركة", "Company", "Société")}
              </span>
              <span className="text-sm font-bold truncate" style={{ color: "#123A63" }}>
                {currentCompanyLabel}
              </span>
            </span>
            <ChevronDown
              className="h-4 w-4 shrink-0 transition-transform duration-200 group-hover:translate-y-[1px]"
              style={{ color: "#1C75BC" }}
            />
            <select
              aria-label={pick(lang, "اختر الشركة", "Select company", "Choisir la société")}
              value={filter}
              onChange={(e) => { setUserInteracted(true); setFilter(e.target.value as Filter); }}
              className="absolute inset-0 opacity-0 cursor-pointer"
              dir={isRtl ? "rtl" : "ltr"}
            >
              <option value="all">{pick(lang, "كل الشركات", "All companies", "Toutes les sociétés")}</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {companyName(c)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Report header */}
        <div className="text-center pt-6 pb-4 px-5">
          <div className="flex items-center justify-center gap-2">
            <h3 className="text-xl font-bold" style={{ color: "#123A63" }}>
              {pick(lang, "قائمة الدخل", "Income Statement", "Compte de résultat")}
            </h3>
            <span
              className="text-xs font-semibold px-2.5 py-0.5 rounded-md"
              style={{ background: "#EAF2FB", color: "#1C6AAE" }}
            >
              {badge}
            </span>
          </div>
          <div className="mt-1 text-xs" dir="ltr" style={{ color: "#8698AC" }}>
            2026-01-01 → 2026-12-31
          </div>
        </div>

        {/* Table */}
        <div className="px-2 sm:px-5 pb-6">
          <div
            className="transition-opacity"
            style={{ opacity, transitionDuration: "180ms" }}
          >
            <table className="w-full border-collapse table-fixed">
              <thead>
                <tr style={{ background: "#F4F8FC" }}>
                  {!isRtl && (
                    <th
                      scope="col"
                      className="text-xs font-semibold py-3 px-2 sm:px-4"
                      style={{ color: "#3D6FA0", ...textAlignStart }}
                    >
                      {pick(lang, "البند", "Item", "Poste")}
                    </th>
                  )}
                  {(isRtl ? cols.slice().reverse() : cols).map((c) => (
                    <th
                      key={c.key}
                      scope="col"
                      className="text-center text-xs font-semibold py-3 px-2 sm:px-4"
                      style={{ color: "#3D6FA0" }}
                    >
                      {c.label}
                    </th>
                  ))}
                  {isRtl && (
                    <th
                      scope="col"
                      className="text-xs font-semibold py-3 px-2 sm:px-4"
                      style={{ color: "#3D6FA0", ...textAlignStart }}
                    >
                      {pick(lang, "البند", "Item", "Poste")}
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => {
                  const rowStyle: React.CSSProperties =
                    l.kind === "gross"
                      ? { background: "#F4F9FE", color: "#1C6AAE", fontWeight: 700 }
                      : l.kind === "net"
                        ? { background: "#EAF6EF", color: "#12894F", fontWeight: 700 }
                        : { color: "#123A63" };
                  const labelFontSize = l.kind === "net" ? "0.98rem" : "0.9rem";
                  const label = pick(lang, l.labelAr, l.labelEn, l.labelFr);
                  return (
                    <tr
                      key={l.key}
                      style={{
                        ...rowStyle,
                        borderTop: "1px solid #F0F4F8",
                      }}
                    >
                      {!isRtl && (
                        <th
                          scope="row"
                          className="py-3 px-2 sm:px-4 font-medium"
                          style={{ fontSize: labelFontSize, ...textAlignStart }}
                        >
                          {label}
                        </th>
                      )}
                      {(isRtl ? cols.slice().reverse() : cols).map((c) => (
                        <td
                          key={c.key}
                          className="text-center py-3 px-2 sm:px-4"
                          style={{ fontSize: labelFontSize }}
                        >
                          <span dir="ltr">{fmt(c.getValue(l))}</span>
                        </td>
                      ))}
                      {isRtl && (
                        <th
                          scope="row"
                          className="py-3 px-2 sm:px-4 font-medium"
                          style={{ fontSize: labelFontSize, ...textAlignStart }}
                        >
                          {label}
                        </th>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-center text-xs mt-5" style={{ color: "#8698AC" }}>
            {pick(
              lang,
              "اعرض القوائم منفصلة لكل شركة، أو مجمّعة في عمود واحد — بنفس الضغطة",
              "View statements separated per company, or consolidated in a single column — in the same click",
              "Affichez les états séparés par société, ou consolidés dans une seule colonne — en un seul clic",
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

export default MultiCompanyIncomeStatement;
